"use client";

import { type DragEvent, useRef, useState } from "react";

import { FILE_TYPE_PRESETS } from "@unreserved/constants";

import { DocumentUploadIcon } from "@/app/_components/icons";
import { cn } from "@/lib/utils";

import { Button } from "./button";

type FileTypePreset = keyof typeof FILE_TYPE_PRESETS;

interface FileUploadProps {
  onFilesSelected: (files: File[]) => void;
  fileTypePreset?: FileTypePreset;
  acceptedFileTypes?: string[];
  customFileTypeLabel?: string;
  maxFileSizeMB?: number;
  multiple?: boolean;
}

export function FileUploadCard({
  onFilesSelected,
  acceptedFileTypes,
  fileTypePreset = "documents",
  customFileTypeLabel,
  maxFileSizeMB = 30,
  multiple = true,
}: FileUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Determining which file types to use
  const fileConfig = acceptedFileTypes
    ? {
        extensions: acceptedFileTypes,
        label: customFileTypeLabel || acceptedFileTypes.join(", ").toUpperCase(),
      }
    : FILE_TYPE_PRESETS[fileTypePreset];

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    onFilesSelected(files);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      onFilesSelected(files);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      className={cn(
        "border-border-400 relative w-full rounded-md border border-dashed pb-4 transition-colors",
        isDragging && "border-primary-600 bg-primary-50",
      )}>
      <div className="bg-secondary-0 mb-10 rounded-md px-3 py-2.5">
        <h3 className="text-typography-900 text-sm md:text-base">Upload Receipt</h3>
        <p className="text-typography-700 text-xs">You can upload multiple files</p>
      </div>
      <div className="px-2md:px-0 mb-2 flex w-full flex-col items-center overflow-clip rounded-[inherit]">
        <div className="bg-primary-100 items-center justify-center rounded-full p-2.5 md:mb-3 md:p-3">
          <DocumentUploadIcon className="size-6 md:size-8" />
        </div>
        <Button
          variant="ghost"
          onClick={handleBrowseClick}
          className="text-primary-600 hover:text-primary-600 m-0 mb-1 h-auto cursor-pointer p-0 text-sm font-semibold">
          Click to upload
        </Button>

        <p className="text-typography-600 w-full px-2 text-center text-xs leading-normal font-normal">
          {customFileTypeLabel || fileConfig.label} {maxFileSizeMB} MB
        </p>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        multiple={multiple}
        accept={fileConfig.extensions.join(",")}
        onChange={handleFileInputChange}
        className="hidden"
      />
    </div>
  );
}
